/**
 * HR "Leaves" hub — /hr/leaves.
 *
 * One sidebar entry consolidating everything HR does with leave, behind
 * in-page tabs:
 *   • Applied Leaves          — org-wide leave list + cancel-approved (HrLeavesPage)
 *   • Leave Entitlement       — per-employee entitlement roster + credit admin:
 *                               adjust, defaults, annual reset, import (EntitlementTab)
 *   • Leave Remaining Balance — read-only wide roster, all types (BalancesTab)
 *   • Replacement Grants      — grant / edit / cancel replacement leave
 *                               (HrReplacementGrantsPage)
 *   • Out of System           — HR-encoded leave taken outside the system (OutOfSystemTab)
 *
 * Replaces the former separate "Leave Applications" and "Adjustments" nav
 * items. Gated on `flags.hrLeaves` (kill-switch, default ON) — when off the
 * whole surface is hidden, there is no legacy fallback.
 */
import { HrAccessGuard } from '../../components/hr/HrAccessGuard'
import { flags } from '../../lib/feature-flags'
import { TabbedHub, type HubTab } from '../hr-hub/TabbedHub'
import { HrLeavesPage } from '../HrLeavesPage'
import { HrReplacementGrantsPage } from '../HrReplacementGrantsPage'
import { BalancesTab } from './BalancesTab'
import { EntitlementTab } from './EntitlementTab'
import { OutOfSystemTab } from './OutOfSystemTab'

const TABS: HubTab[] = [
  { key: 'applications', label: 'Applied Leaves', render: () => <HrLeavesPage /> },
  { key: 'entitlement', label: 'Leave Entitlement', render: () => <EntitlementTab /> },
  { key: 'balances', label: 'Leave Remaining Balance', render: () => <BalancesTab /> },
  {
    key: 'replacement',
    label: 'Replacement Grants',
    flag: 'replacementGrantsAdminUi',
    render: () => <HrReplacementGrantsPage />,
  },
  {
    key: 'out-of-system',
    label: 'Out of System',
    flag: 'outOfSystemLeave',
    render: () => <OutOfSystemTab />,
  },
]

export function HrLeavesHub(): JSX.Element | null {
  if (!flags.hrLeaves) return null
  return (
    <HrAccessGuard>
      {/* Match the My Leaves content width + responsive side gutters (`.page`:
          max-width 1280 + clamp(32px, 6vw, 96px) horizontal padding) so the
          hub doesn't run edge-to-edge in the wider AppShell container. */}
      <div className="page">
        <TabbedHub testIdPrefix="leaves" routePath="/hr/leaves" heroTitle="Leaves" tabs={TABS} />
      </div>
    </HrAccessGuard>
  )
}
